import { inject } from '@angular/core';
import { EntitlementsService } from 'app/core/entitlements/entitlements.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { MessagesService } from 'app/layout/common/messages/messages.service';
import { NotificationsService } from 'app/layout/common/notifications/notifications.service';
import { QuickChatService } from 'app/layout/common/quick-chat/quick-chat.service';
import { ShortcutsService } from 'app/layout/common/shortcuts/shortcuts.service';
import { catchError, forkJoin, of, switchMap, tap } from 'rxjs';

/**
 * The plan is loaded before the menu is built.
 *
 * The menu drops the other operating rule's modules and padlocks the ones
 * this plan cannot reach — and it can do neither while the plan is still
 * "unknown". Built in the wrong order, a Part 91 owner was shown Quotes and
 * every locked module arrived unlocked.
 */
export const initialDataResolver = () => {
    const entitlements = inject(EntitlementsService);
    const messagesService = inject(MessagesService);
    const navigationService = inject(NavigationService);
    const notificationsService = inject(NotificationsService);
    const quickChatService = inject(QuickChatService);
    const shortcutsService = inject(ShortcutsService);

    // A device that has seen the plan before opens with it at once and learns
    // the current one in the background, rebuilding the menu if it changed.
    // Only the very first visit waits for the server.
    const plan$ = entitlements.restoredFromDevice
        ? of(null).pipe(
              tap(() =>
                  entitlements
                      .load()
                      .pipe(catchError(() => of(null)))
                      .subscribe(() => navigationService.refresh())
              )
          )
        : entitlements.load();

    return plan$.pipe(
        // A plan that cannot be read is not a reason to refuse the app: the
        // server enforces the real limits either way.
        catchError(() => of(null)),
        switchMap(() =>
            forkJoin([
                navigationService.get(),
                messagesService.getAll(),
                notificationsService.getAll(),
                quickChatService.getChats(),
                shortcutsService.getAll(),
            ])
        )
    );
};
